import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { supabase } from "../supabaseClient"; // Import supabase client
import moment from "moment-timezone";
import { useTimezone } from "../Context/TimezoneContext";
import { storeMeetingChat, uploadAudioAndGetUrl, storeMeetingChatFirstResponse } from "../chatHistory"; // Add uploadAudioAndGetUrl
import AddToSessionModal from "./Addtosessionmodal";
import MentionsInput from "./common/MentionsInput";
import MeetingCreatesessionmodal from "./sidebar/MeetingCreatesessionmodal";
import Record from "./Record"; // Add Record component import
import ImageviewModal from "./common/ImageviewModal"; // Add ImageviewModal import
import ModeSelector, { getSelectedMode } from "./common/ModeSelector";
import { getContextLimitsForPayload } from "../utils/contextLimits";

import { uploadFileAndGetUrl } from "../chatHistory"; // Add file upload function

// Helper function to get file icon based on file extension
const getFileIcon = (fileName, size = 24, color = "currentColor") => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  const iconStyles = { width: size, height: size, flexShrink: 0 };

  // PDF Icon - Red
  if (extension === "pdf") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H11V8H7V10ZM15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#E53935"/>
        <path d="M14 3V9H20L14 3Z" fill="#FFCDD2"/>
        <text x="12" y="19" fontSize="6" fontWeight="bold" fill="white" textAnchor="middle">PDF</text>
      </svg>
    );
  }

  // Excel/Spreadsheet Icon - Green
  if (["xlsx", "xls", "csv"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#43A047"/>
        <path d="M14 3V9H20L14 3Z" fill="#C8E6C9"/>
        <path d="M7 13H9V15H7V13ZM10 13H12V15H10V13ZM13 13H15V15H13V13ZM7 16H9V18H7V16ZM10 16H12V18H10V16ZM13 16H15V18H13V16Z" fill="white"/>
      </svg>
    );
  }

  // Word Document Icon - Blue
  if (["docx", "doc"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#1976D2"/>
        <path d="M14 3V9H20L14 3Z" fill="#BBDEFB"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H13V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Text File Icon - Gray
  if (extension === "txt") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#757575"/>
        <path d="M14 3V9H20L14 3Z" fill="#E0E0E0"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H12V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Default Document Icon
  return (
    <svg viewBox="0 0 24 24" fill={color} style={iconStyles}>
      <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2ZM19 20H5V4H14V9H19V20Z"/>
    </svg>
  );
};

// Helper function to get file type from file name
const getFileTypeFromName = (fileName) => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  if (["jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") {
    return "pdf";
  }
  if (["xlsx", "xls"].includes(extension)) {
    return "excel";
  }
  if (extension === "csv") {
    return "csv";
  }
  if (["docx", "doc"].includes(extension)) {
    return "word";
  }
  if (extension === "txt") {
    return "txt";
  }
  return "document";
};

function MeetingChatModal({ show, onHide, meeting_summaries_id, userId }) {
  // Get user timezone from context
  const { userTimezone } = useTimezone();

  const [replyMessage, setReplyMessage] = useState("");
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [zoomeyApiUrl] = useState(process.env.REACT_APP_ZOOMEY_API_URL);
  const [apiToken, setApiToken] = useState([]);

  const modalBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const mentionsInputRef = useRef(null);
  const controllerRef = useRef(null); // AbortController for stop functionality

  // Thinking animation state
  const [responseInformMessage] = useState("Thinking");

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // Add these states at the top of MeetingChatModal function
  const [showMeetingSessionModal, setShowMeetingSessionModal] = useState(false);
  const [sideConvoList, setSideConvoList] = useState([]);

  const [showAddToSessionModal, setShowAddToSessionModal] = useState(false);
  const [addToChatText, setAddToChatText] = useState("");

  // Add audio-related states for Meeting Modal
  const [audioInput, setAudioInput] = useState(null); // Audio input from Record component
  // Audio processing state
  const [isLoading, setIsLoading] = useState(false); // General loading state
  const [isSending, setIsSending] = useState(false); // Sending state
  const [isStreaming, setIsStreaming] = useState(false); // Streaming state for main Zoomey
  const [isUserQueyLoading, setIsUserQueyLoading] = useState(false); // User query loading
  const currentStreamingContentRef = useRef(""); // Track streaming content for main Zoomey
  const [isDarkModeInternal] = useState(false); // Dark mode state
  const [meetingEnabled, setMeetingEnabled] = useState(true); // Local toggle for enable/disable mode
  const [selectedMode, setSelectedMode] = useState(() => getSelectedMode());

  // Add file upload states - exactly like ReplyModal
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  // removed unused selectedFile/uploadedFile states
  const fileInputRef = useRef(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("image");
  const [selectedFileName, setSelectedFileName] = useState("");

  const [audioRecording, setAudioRecording] = useState(false);
  const [cancelAudio, setCancelAudio] = useState(false);
  const [copyToTextArea, setCopyToTextArea] = useState(false);
  const [isAudioProcessing, setIsAudioProcessing] = useState(false);

  function formatMentionSpacing(text) {
    return text.replace(/(@\w+):\s*/g, "$1: ");
  }

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = modalBodyRef.current.scrollHeight;
    }
  }, [selectedMeeting]);

  const formatResponseMessage = (text) => {
    if (!text) return "";
    // If text is already HTML (from backend formatToHTML), linkify any remaining plain URLs
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return text.replace(/((?:^|[^"'=/>]))(https?:\/\/[^\s<>\[\]"']+)/g,
        (match, prefix, url) => `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      );
    }
    let html = text;

    // Protect URLs from double-processing
    const urlPlaceholders = [];
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, linkText, url) => {
      urlPlaceholders.push({ type: "markdown", text: linkText, url });
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });
    html = html.replace(/(https?:\/\/[^\s<>\[\]()]+)/g, (match) => {
      urlPlaceholders.push({ type: "plain", url: match });
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });

    html = html
      .replace(/###\s*(.*?)(?:\n|$)/g, "<h5>$1</h5>")
      .replace(/##\s*(.*?)(?:\n|$)/g, "<h4>$1</h4>")
      .replace(/#\s*(.*?)(?:\n|$)/g, "<h3>$1</h3>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/(\d+)\.\s+(.*?)(?:\n|$)/g, '<ol start="$1"><li>$2</li></ol>')
      .replace(
        /`(.*?)`/g,
        '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>'
      );

    // Restore URLs as proper <a> tags
    html = html.replace(/URLPLACEHOLDER(\d+)ENDURL/g, (match, index) => {
      const entry = urlPlaceholders[parseInt(index)];
      if (entry.type === "markdown") {
        return `<a href="${entry.url}" target="_blank" rel="noopener noreferrer">${entry.text}</a>`;
      }
      return `<a href="${entry.url}" target="_blank" rel="noopener noreferrer">${entry.url}</a>`;
    });

    html = html.replace(/\{(.*?)\}/g, "$1");
    return html;
  };

  const getTimeZone = (created_at) => {
    return moment
      .utc(created_at)
      .tz(userTimezone)
      .format("MM-DD-YYYY hh:mm A");
  };

  const getPopupContent = async (requestId) => {
    try {
      const { data, error } = await supabase
        .from("meeting_summaries_chat")
        .select("*, path") // Use 'path' column like ReplyModal
        .eq("meeting_summaries_id", requestId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching meeting chat:", error);
        return;
      }

      // Map the data exactly like ReplyModal
      const mappedData =
        data?.map((item) => {
          const hasAudio =
            item.path &&
            item.path.includes("/audios/") &&
            item.path.trim() !== "";
          const hasFile =
            item.path &&
            !item.path.includes("/audios/") &&
            item.path.trim() !== "";
          const hasText = item.user_query && item.user_query.trim() !== "";

          // Check if it's a file message (with or without text) - like ReplyModal
          if (hasFile && hasText) {
            // Check if user_query contains file reference
            const isFileReference =
              item.user_query.includes("Shared image:") ||
              item.user_query.includes("Shared file:") ||
              item.user_query.includes(".jpg") ||
              item.user_query.includes(".png") ||
              item.user_query.includes(".pdf") ||
              item.user_query.includes(".doc");

            if (isFileReference) {
              // Pure file message
              return {
                ...item,
                audioUrl: null,
                fileUrl: item.path,
                isFileMessage: true,
                fileName: item.user_query
                  .replace("Shared image: ", "")
                  .replace("Shared file: ", ""),
                fileType: item.user_query.includes("Shared image:")
                  ? "image/"
                  : "application/",
              };
            } else {
              // File with text message - show both
              return {
                ...item,
                audioUrl: null,
                fileUrl: item.path,
                isFileMessage: true,
                fileName: item.path.split("/").pop() || "file",
                fileType: item.path.includes("images/")
                  ? "image/"
                  : "application/",
                hasText: true, // Flag to show text along with file
              };
            }
          }
          // If message has both audio and text, we need to create separate entries
          else if (hasAudio && hasText) {
            return [
              // Audio-only message
              {
                ...item,
                id: `${item.id}_audio`,
                audioUrl: item.path,
                user_query: "", // Empty text for audio-only
                isAudioOnly: true,
              },
              // Text-only message
              {
                ...item,
                id: item.id,
                audioUrl: null,
                path: null,
                isTextOnly: true,
              },
            ];
          } else if (hasAudio && !hasText) {
            // Audio-only message
            return {
              ...item,
              audioUrl: item.path,
              isAudioOnly: true,
            };
          } else {
            // Text-only message
            return {
              ...item,
              audioUrl: item.path, // Keep for backward compatibility
            };
          }
        }) || [];

      // Flatten the array in case we have nested arrays from audio+text messages
      const flattenedData = mappedData.flat();

      setSelectedMeeting(flattenedData);
      console.log("Fetched meeting data:", flattenedData);
    } catch (error) {
      console.error("Error in getPopupContent:", error);
    }
  };

  useEffect(() => {
    if (meeting_summaries_id) {
      console.log("MeetingModal meeting_summaries_id", meeting_summaries_id);
      initializeMeetingChat(meeting_summaries_id);
      storeApiToken();
    }
  }, [meeting_summaries_id]);

  // Initialize meeting chat - store first response if no entry exists, then fetch
  const initializeMeetingChat = async (meetingData) => {
    try {
      // Store first response if no entry exists (will return null if already exists)
      await storeMeetingChatFirstResponse(
        meetingData?.id,
        meetingData,  // Pass full meeting data object
        userId
      );

      // Now fetch the chat content
      getPopupContent(meetingData?.id);
    } catch (error) {
      console.error("Error initializing meeting chat:", error);
      // Still try to fetch content even if storing fails
      getPopupContent(meetingData?.id);
    }
  };

  const storeApiToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      console.warn("No session found. User may not be logged in.");
      return;
    }
    setApiToken(session.access_token);
  };

  // Add audio processing functions for Meeting Modal
  const audioToText = async (audioInput) => {
    try {
      // Get fresh session token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("No valid session found. Please log in again.");
      }

      const formData = new FormData();
      formData.append("file", audioInput, "recording.mp3");

      const response = await fetch(zoomeyApiUrl + "/speech-to-text", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          // Don't include Content-Type - browser sets it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Upload failed: ${response.statusText} - ${
            errorData.error?.message || ""
          }`
        );
      }

      const data = await response.json();
      return data.text;
    } catch (error) {
      console.error("Error transcribing audio:", error);
      return null;
    }
  };

  // Handle audio input for Meeting Modal
  // useEffect(() => {
  //   if (audioInput) {
  //     handleMeetingAudioMessage();
  //   }

  // }, [audioInput]);

  useEffect(() => {
    if (!audioInput) return;
    (async () => {
      try {
        if (copyToTextArea) {
          setIsAudioProcessing(true); // Footer loader ON
          const transcript = await audioToText(audioInput);
          if (transcript) {
            setReplyMessage((prev) =>
              prev ? prev + " " + transcript : transcript
            );
            if (textareaRef.current) textareaRef.current.focus();
          }
          setIsAudioProcessing(false); // Footer loader OFF
          setAudioInput(null);
          setCopyToTextArea(false);
          return;
        }
        // Direct send flow: NO footer loader, only chat bubble loader
        setIsAudioProcessing(false); // Ensure footer loader OFF
        await handleMeetingAudioMessage();
      } catch (err) {
        setIsAudioProcessing(false);
        setAudioInput(null);
        setCopyToTextArea(false);
      }
    })();
  }, [audioInput, copyToTextArea]);

  useEffect(() => {
    if (!cancelAudio) return;
    setAudioInput(null);
    setIsAudioProcessing(false);
    setCopyToTextArea(false);
    setCancelAudio(false);
  }, [cancelAudio]);

  const handleMeetingAudioMessage = async () => {
    try {
      if (audioInput != null) {
        setIsLoading(true);
        // setIsAudioProcessing(true);
        console.log("Processing meeting audio input...");

        // Show temporary audio processing message in chat
        const tempProcessingId = `temp-${Date.now()}`;
        setSelectedMeeting((prev) => {
          const currentList = prev || [];
          return [
            ...currentList,
            {
              id: tempProcessingId,
              user_query: "", // Empty query for now
              audioUrl: null,
              isProcessing: true, // Flag to show processing state
              createdAt: new Date().toISOString(),
              response: null,
            },
          ];
        });

        // Convert audio to text
        const audioText = await audioToText(audioInput);
        console.log("Meeting audio transcribed:", audioText);

        if (audioText) {
          // Upload audio to Supabase storage
          let supabaseAudioUrl = null;
          try {
            supabaseAudioUrl = await uploadAudioAndGetUrl(
              meeting_summaries_id?.id,
              userId,
              audioInput
            );
            console.log("Meeting audio uploaded to:", supabaseAudioUrl);
          } catch (uploadError) {
            console.error("Meeting audio upload failed:", uploadError);
            // Continue without audio URL if upload fails
          }

          // Combine input area text + transcript
          let finalText = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
          mentionsInputRef.current?.clearMention();
          if (audioText) {
            finalText = finalText
              ? `${finalText} ${audioText}` // Combine both
              : audioText; // Only transcript if input empty
          }

          // Store the meeting chat with audio URL
          const dataGet = await storeMeetingChat(
            meeting_summaries_id?.id,
            finalText, // <-- Combined text+transcript
            supabaseAudioUrl
          );
          console.log("Meeting chat stored:", dataGet);

          let finalAudioText = audioText;
          if (selectedMeeting && selectedMeeting.length <= 1) {
            // Account for temp message
            const meetingContext = `For information, this is the meeting summary:
              
            Meeting Title: ${meeting_summaries_id?.meeting_title || "N/A"}
            Meeting Date: ${meeting_summaries_id?.meeting_date || "N/A"}
            Overview: ${meeting_summaries_id?.overview || "N/A"}
            Short Summary: ${meeting_summaries_id?.short_summary || "N/A"}
            Gist: ${meeting_summaries_id?.gist || "N/A"}
            Key Points: ${meeting_summaries_id?.shorthand_bullet || "N/A"}
            Bullet Gist: ${meeting_summaries_id?.bullet_gist || "N/A"}

            User Query: ${audioText}`;

            finalAudioText = meetingContext;
          }

          // Remove temporary processing message and add real messages
          setSelectedMeeting((prev) => {
            const currentList = prev || [];
            const withoutTemp = currentList.filter(
              (item) => item.id !== tempProcessingId
            );
            return [
              ...withoutTemp,
              {
                id: `${dataGet[0].id}_audio`,
                user_query: "",
                audioUrl: supabaseAudioUrl,
                path: supabaseAudioUrl,
                createdAt: new Date().toISOString(),
                response: null,
                isAudioOnly: true,
              },
              {
                id: dataGet[0].id,
                user_query: finalText, // <-- Yeh combined text+transcript hai!
                audioUrl: null,
                path: null,
                createdAt: new Date().toISOString(),
                response: null,
                isTextOnly: true,
              },
            ];
          });

          // setIsAudioProcessing(false);
          setReplyMessage("");

          // When toggle is disabled, route to main Zoomey with full tools
          if (!meetingEnabled) {
            // Update UI to mark as streaming response
            setSelectedMeeting((prev) => {
              const currentList = prev || [];
              return currentList.map((item) =>
                item.id === dataGet[0].id
                  ? { ...item, isStreamingResponse: true }
                  : item
              );
            });
            await sendToMainZoomey(finalText, dataGet[0].id);
            return;
          }

          const payload = {
            user_id: userId,
            session_id: meeting_summaries_id?.id.toString(),
            store_id: dataGet[0].id.toString(),
            user_query: finalAudioText,
          };

          // Refresh token before API call
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            setApiToken(session.access_token);
          }

          // Send to API
          const response = await fetch(
            zoomeyApiUrl + "/meeting_summaries_chat",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${session.access_token}`,
              },
              body: JSON.stringify(payload),
            }
          );

          if (response.status === 200) {
            console.log("Meeting audio API call successful");
            // Refresh the full conversation after response
            setTimeout(() => {
              getPopupContent(meeting_summaries_id?.id);
            }, 1000);
          } else {
            console.error(
              "Meeting audio API call failed with status:",
              response.status
            );
            // alert("Something went wrong with your server.");
          }
        }
      }
    } catch (error) {
      console.error("Meeting audio processing error:", error);
      // alert("Failed to process audio. Please try again.");
      // Remove temp processing message on error
      setSelectedMeeting((prev) => {
        const currentList = prev || [];
        return currentList.filter((item) => !item.isProcessing);
      });
    } finally {
      setIsLoading(false);
      // setIsAudioProcessing(false);
      setAudioInput(null);
    }
  };

  // Add this function to handle audio recording stop
  const handleMeetingAudioRecorded = async (audioUrl) => {
    try {
      setIsLoading(true);
      console.log("Meeting audio recorded:", audioUrl);
    } catch (error) {
      console.error("Error handling meeting recorded audio:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // File upload functions - exactly like ReplyModal
  const handleFileSelect = async (e) => {
    const file = e.target.files[0]; // Get only the first file
    if (file) {
      setSelectedFiles([file]); // Replace existing files with new single file

      // Create preview for the single file
      let preview;
      if (file.type.startsWith("image/")) {
        preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) =>
            resolve({
              file,
              type: "image",
              url: e.target.result,
              name: file.name,
            });
          reader.readAsDataURL(file);
        });
      } else {
        preview = {
          file,
          type: getFileTypeFromName(file.name),
          url: null,
          name: file.name,
        };
      }

      setFilePreviews([preview]); // Replace existing previews with new single preview
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSendWithFiles = async () => {
    if (selectedFiles.length === 0 && replyMessage.trim() === "") return;

    try {
      setIsSending(true);

      const fileToSend = selectedFiles[0]; // Get the single file
      const messageToSend = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
      mentionsInputRef.current?.clearMention();

      if (fileToSend) {
        // Process the single file
        const fileUrl = await uploadFileAndGetUrl(
          meeting_summaries_id?.id,
          userId,
          fileToSend
        );

        if (!fileUrl) {
          console.error("File upload failed!");
          // Clear UI immediately even on error
          setSelectedFiles([]);
          setFilePreviews([]);
          setReplyMessage("");
          return;
        }

        // Compose the message for chat history (for UI only)
        let messageText = messageToSend;
        if (!messageText) {
          if (fileToSend.type.startsWith("image/")) {
            messageText = `Shared image: ${fileToSend.name}`;
          } else {
            messageText = `Shared file: ${fileToSend.name}`;
          }
        }

        // Store file message in database - fileUrl goes to path column like ReplyModal
        const dataGet = await storeMeetingChat(
          meeting_summaries_id?.id,
          messageText,
          fileUrl // This goes to path column
        );

        // Clear UI immediately after sending (before waiting for response)
        setSelectedFiles([]);
        setFilePreviews([]);
        setReplyMessage("");

        // Update local state immediately with file message - like ReplyModal
        setSelectedMeeting((prev) => [
          ...(prev || []),
          {
            id: dataGet[0].id,
            user_query: messageText,
            audioUrl: null,
            path: fileUrl,
            createdAt: new Date().toISOString(),
            response: null,
            isFileMessage: true,
            fileName: fileToSend.name,
            fileType: fileToSend.type,
            fileUrl: fileUrl,
            hasText: messageToSend ? true : false,
            isStreamingResponse: !meetingEnabled, // Mark as streaming if using main Zoomey
          },
        ]);

        // When toggle is disabled, route to main Zoomey with full tools
        if (!meetingEnabled) {
          await sendToMainZoomey(messageToSend || "", dataGet[0].id, fileUrl);
          return;
        }

        // Add meeting context for first message (for UI only)
        let finalMessage = messageText;
        if (selectedMeeting && selectedMeeting.length === 0) {
          const meetingContext = `For information, this is the meeting summary:\nMeeting Title: ${
            meeting_summaries_id?.meeting_title || "N/A"
          }\nMeeting Date: ${
            meeting_summaries_id?.meeting_date || "N/A"
          }\nOverview: ${
            meeting_summaries_id?.overview || "N/A"
          }\nShort Summary: ${
            meeting_summaries_id?.short_summary || "N/A"
          }\nGist: ${meeting_summaries_id?.gist || "N/A"}\nKey Points: ${
            meeting_summaries_id?.shorthand_bullet || "N/A"
          }\nBullet Gist: ${
            meeting_summaries_id?.bullet_gist || "N/A"
          }\nUser Query: ${finalMessage}`;
          finalMessage = meetingContext;
        }

        // Add Attachment_URL to user_query same as ChatBot format
        let userQueryWithAttachment = messageToSend || "";

        if (fileUrl) {
          userQueryWithAttachment = userQueryWithAttachment
            ? userQueryWithAttachment + `\n Attachment_URL: [${fileUrl}]`
            : `Attachment_URL: [${fileUrl}]`;
        }

        // Prepare payload for main API (no file object needed, Attachment_URL is in user_query)
        const payload = {
          user_id: userId,
          session_id: meeting_summaries_id?.id.toString(),
          store_id: dataGet[0].id.toString(),
          user_query: userQueryWithAttachment,
        };

        // Refresh token before API call
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setApiToken(session.access_token);
        }

        const response = await fetch(zoomeyApiUrl + "/meeting_summaries_chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 200) {
          console.log("File message sent successfully");
        } else {
          console.error("File message API failed:", response.status);
        }
      } else if (messageToSend !== "") {
        // Send text message only
        await sendChatMessage(messageToSend);
        // Clear UI immediately after sending text
        setReplyMessage("");
      }

      // Refresh conversation
      setTimeout(() => {
        getPopupContent(meeting_summaries_id?.id);
      }, 1000);
    } catch (error) {
      console.error("Error sending files:", error);
      // alert("Failed to upload files. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const openFileModal = (fileUrl, fileType = "image", fileName = "File") => {
    setSelectedFileUrl(fileUrl);
    setSelectedFileType(fileType);
    setSelectedFileName(fileName);
    setShowFileModal(true);
  };

  // Handle stop request - cancel ongoing API call
  const handleStopRequest = async () => {
    setIsLoading(false);
    setIsSending(false);
    setIsStreaming(false);

    // Cancel the actual API request
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    // Update is_thinking to false in DB
    await supabase
      .from("meeting_summaries")
      .update({ is_thinking: false })
      .eq("id", meeting_summaries_id?.id);

    // Show stopped message
    const stoppedMessage = '<span style="color: #888; font-style: italic;">⏹️ You stopped this request</span>';

    // Update last message with stopped indicator
    setSelectedMeeting((prev) => {
      const currentList = prev || [];
      const lastIndex = currentList.length - 1;
      if (lastIndex >= 0 && !currentList[lastIndex].response) {
        const updated = [...currentList];
        updated[lastIndex] = { ...updated[lastIndex], response: stoppedMessage };
        return updated;
      }
      return currentList;
    });
  };

  // Send message to meeting_zoomey_chat endpoint with full tools (when meeting toggle is disabled)
  const sendToMainZoomey = async (message, storeId, fileUrl = null) => {
    try {
      // Build user query with optional file attachment
      let userQuery = message;
      if (fileUrl) {
        userQuery = message
          ? `${message}\n Attachment_URL: [${fileUrl}]`
          : `Attachment_URL: [${fileUrl}]`;
      }

      const payload = {
        user_id: userId,
        session_id: meeting_summaries_id?.id.toString(),
        store_id: storeId.toString(),
        user_query: userQuery,
        mode: selectedMode,
        customContextLimits: getContextLimitsForPayload(),
      };

      // Refresh token before API call
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        setApiToken(session.access_token);
      }

      setIsStreaming(true);

      // Set is_thinking = true before API call
      await supabase
        .from("meeting_summaries")
        .update({ is_thinking: true })
        .eq("id", meeting_summaries_id?.id);

      // Create AbortController for this request
      controllerRef.current = new AbortController();

      // Use new meeting_zoomey_chat endpoint instead of run_sse
      const response = await fetch(zoomeyApiUrl + "/api/meeting_zoomey_chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
        signal: controllerRef.current.signal,
      });

      // Handle JSON response instead of SSE stream
      const data = await response.json();

      if (!response.ok || !data.success) {
        const errorText = data.error || "Error processing request";
        console.error("[sendToMainZoomey] Error:", errorText);
        
        // Update UI with error
        setSelectedMeeting((prev) => {
          const currentList = prev || [];
          return currentList.map((item) =>
            item.id === storeId && item.isStreamingResponse
              ? { ...item, response: `⚠️ ${errorText}`, isStreamingResponse: false }
              : item
          );
        });
        
        return `⚠️ ${errorText}`;
      }

      const fullResponse = data.response;

      // Final UI update - remove streaming flag and set response
      setSelectedMeeting((prev) => {
        const currentList = prev || [];
        return currentList.map((item) =>
          item.id === storeId && item.isStreamingResponse
            ? { ...item, response: fullResponse, isStreamingResponse: false }
            : item
        );
      });

      return fullResponse;
    } catch (error) {
      console.error("[sendToMainZoomey] Error:", error);
      
      // Update UI with error
      setSelectedMeeting((prev) => {
        const currentList = prev || [];
        return currentList.map((item) =>
          item.id === storeId && item.isStreamingResponse
            ? { ...item, response: `⚠️ Error: ${error.message}`, isStreamingResponse: false }
            : item
        );
      });
      
      return `⚠️ Error: ${error.message}`;
    } finally {
      setIsStreaming(false);
      controllerRef.current = null;
      // Set is_thinking = false after response
      await supabase
        .from("meeting_summaries")
        .update({ is_thinking: false, is_view: true })
        .eq("id", meeting_summaries_id?.id);
    }
  };

  const sendChatMessage = async (message) => {
    setReplyMessage("");

    const formattedMessage = formatMentionSpacing(message); // Format @option: spacing
    if (message.trim() !== "") {
      const dataGet = await storeMeetingChat(
        meeting_summaries_id?.id,
        formattedMessage,
        null
      ); // Pass null for audio URL

      let finalMessage = formattedMessage;
      if (selectedMeeting && selectedMeeting.length === 0) {
        const meetingContext = `For information, this is the meeting summary:
              
        Meeting Title: ${meeting_summaries_id?.meeting_title || "N/A"}
        Meeting Date: ${meeting_summaries_id?.meeting_date || "N/A"}
        Overview: ${meeting_summaries_id?.overview || "N/A"}
        Short Summary: ${meeting_summaries_id?.short_summary || "N/A"}
        Gist: ${meeting_summaries_id?.gist || "N/A"}
        Key Points: ${meeting_summaries_id?.shorthand_bullet || "N/A"}
        Bullet Gist: ${meeting_summaries_id?.bullet_gist || "N/A"}

        User Query: ${message}`;

        finalMessage = meetingContext;
      }

      const payload = {
        user_id: userId,
        session_id: meeting_summaries_id?.id.toString(),
        store_id: dataGet[0].id.toString(),
        user_query: finalMessage,
      };

      // Update local state immediately - like ReplyModal
      setSelectedMeeting((prev) => [
        ...(prev || []),
        {
          id: dataGet[0].id,
          user_query: message,
          audioUrl: null,
          path: null,
          createdAt: new Date().toISOString(),
          response: null,
          isStreamingResponse: !meetingEnabled, // Mark as streaming if using main Zoomey
        },
      ]);

      // When toggle is disabled, route to main Zoomey with full tools
      if (!meetingEnabled) {
        await sendToMainZoomey(formattedMessage, dataGet[0].id);
        return;
      }

      // Refresh token before API call
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        setApiToken(session.access_token);
      }

      setIsSending(true);

      // Set is_thinking = true before API call
      await supabase
        .from("meeting_summaries")
        .update({ is_thinking: true })
        .eq("id", meeting_summaries_id?.id);

      // Create AbortController for this request
      controllerRef.current = new AbortController();

      try {
        const response = await fetch(zoomeyApiUrl + "/meeting_summaries_chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
          signal: controllerRef.current.signal,
        });

        if (response.status === 200) {
          console.log("Text message sent successfully");
          // Refresh the full conversation after response
          setTimeout(() => {
            getPopupContent(meeting_summaries_id?.id);
          }, 1000);
        } else {
          console.error("Text message API failed with status:", response.status);
        }
      } finally {
        setIsSending(false);
        controllerRef.current = null;
        // Set is_thinking = false after response
        await supabase
          .from("meeting_summaries")
          .update({ is_thinking: false, is_view: true })
          .eq("id", meeting_summaries_id?.id);
      }
    }
  };

  // useEffect to fetch sessions
  useEffect(() => {
    const fetchSessions = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        const { data: sessionList, error: parentError } = await supabase
          .from("chat_session")
          .select("*")
          .eq("type", "topic")
          .eq("user_auth", session.user.id)
          .order("updated_at", { ascending: false });

        if (!parentError) {
          setSideConvoList(sessionList);
        }
      }
    };
    fetchSessions();
  }, []);

  const meetingBtnDisabled = isLoading || isSending || isStreaming;

  // return(<> </>)
  return (
    <>
      {/* <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          .audio-processing-spinner {
            animation: spin 1s linear infinite;
          }
        `}
      </style> */}
      <Modal show={show} onHide={onHide} size="xl" centered backdrop="static" className="max_width_modal">
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Modal.Title>Meeting Chat</Modal.Title>
            <Button
              className="queue-create-btn"
              style={{ cursor: "pointer" }}
              onClick={() => setShowMeetingSessionModal(true)}
            >
              Create topic
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body
          className="custom-scrollbar"
          ref={modalBodyRef}
          style={{ maxHeight: "70vh", overflowY: "auto" }}
        >
          {/* {isLoading && (
            <div style={{ 
              position: "absolute", 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: "rgba(255,255,255,0.8)", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              zIndex: 10 
            }}>
              <div style={{ textAlign: "center" }}>
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: "8px", fontSize: "14px" }}>
                  Processing audio...
                </div>
              </div>
            </div>
          )} */}
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: "12px",
                width: "100%",
                position: "relative", // <-- Needed for dropdown positioning
              }}
            >
              <div
                style={{
                  backgroundColor: "transparent",
                  color: "var(--text)",
                  padding: "12px 16px 12px 45px",
                  borderRadius: "12px",
                  maxWidth: "100%",
                  wordBreak: "break-word",
                }}
              >
                {/* 3-dot dropdown for main summary */}
                <div style={{ position: "absolute", top: 8, right: 8 }}>
                  <div className="dropdown">
                    <i
                      className="bi bi-three-dots-vertical"
                      style={{ cursor: "pointer", fontSize: "1.2em" }}
                      id="dropdownMenuButton-summary"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    ></i>
                    <ul
                      className="dropdown-menu"
                      aria-labelledby="dropdownMenuButton-summary"
                    >
                      <li
                        className="dropdown-item"
                        onClick={() => {
                          setShowAddToSessionModal(true);
                          // You can choose what to add: overview, title, etc.
                          setAddToChatText(
                            meeting_summaries_id?.overview ||
                              meeting_summaries_id?.meeting_title ||
                              "No summary available"
                          );
                        }}
                      >
                        <i className="bi bi-plus-square-dotted"></i> Add to
                        Session
                      </li>
                    </ul>
                  </div>
                </div>
                <span
                  className="zoomey-title"
                  style={{ fontWeight: "bold", marginLeft: "-47px" }}
                >
                  <span className="chatbot_z_logo">Z</span>
                  Zoomey
                </span>
                {/* Meeting Title */}
                {meeting_summaries_id?.meeting_title && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Meeting Title
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.meeting_title
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Meeting Date */}
                {meeting_summaries_id?.meeting_date && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Meeting Date
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.meeting_date
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Overview */}
                {meeting_summaries_id?.overview && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Overview
                    </h4>
                    <div
                      style={{ paddingRight: "40px" }}
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.overview
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Short Summary */}
                {meeting_summaries_id?.short_summary && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Short Summary
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.short_summary
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Gist */}
                {meeting_summaries_id?.gist && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Gist
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.gist
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Shorthand Bullet Points */}
                {meeting_summaries_id?.shorthand_bullet && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Key Points
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.shorthand_bullet
                        ),
                      }}
                    />
                  </div>
                )}

                {/* Bullet Gist */}
                {meeting_summaries_id?.bullet_gist && (
                  <div style={{ marginBottom: "12px" }}>
                    <h4
                      style={{
                        marginBottom: "8px",
                        fontWeight: "bold",
                      }}
                    >
                      Bullet Gist
                    </h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: formatResponseMessage(
                          meeting_summaries_id.bullet_gist
                        ),
                      }}
                    />
                  </div>
                )}
                <span>
                  {getTimeZone(meeting_summaries_id?.created_at)}
                </span>
              </div>
            </div>
            {selectedMeeting && selectedMeeting.length > 0 ? (
              selectedMeeting.map((request, index) => (
                <React.Fragment key={request.id || index}>
                  {/* User message (right) - Only show if user_query is not empty */}
                  {(request.user_query && request.user_query.trim() !== "") || request.isAudioOnly || request.isFileMessage || request.isProcessing ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "12px",
                      width: "100%",
                    }}
                  >
                    <div
                      className="chat-user-msg"
                      style={{
                        background: request.isAudioOnly
                          ? "transparent"
                          : "var(--teal-bg)",
                        color: "var(--textwhite)",
                        padding: request.isAudioOnly ? "0" : "12px 16px",
                        borderRadius: "12px",
                        maxWidth: "70%",
                        wordBreak: "break-word",
                      }}
                    >
                      <div
                        style={{
                          paddingRight: request.isAudioOnly ? "0" : "0px",
                        }}
                      >
                        {/* Show processing state for audio */}
                        {request.isProcessing ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "8px 12px",
                              background: "transparent",
                              borderRadius: "12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <i
                                className="bi bi-mic-fill"
                                style={{ fontSize: "14px", opacity: 0.7 }}
                              ></i>
                              <span style={{ fontSize: "12px", opacity: 0.7 }}>
                                Processing voice message
                              </span>
                            </div>
                            <div
                              className="audio-processing-spinner"
                              style={{
                                width: "16px",
                                height: "16px",
                                border: "2px solid rgba(255,255,255,0.3)",
                                borderTop: "2px solid white",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            ></div>
                          </div>
                        ) : request.isAudioOnly ? (
                          /* Audio-only bubble - exactly like ChatBot */
                          <audio
                            controls
                            src={request.audioUrl}
                            style={{
                              maxWidth: "100%",
                              width: "250px", // Fixed width like ChatBot
                            }}
                          />
                        ) : request.isFileMessage ? (
                          /* File message display - exactly like ReplyModal */
                          <div>
                            {request.fileType &&
                            request.fileType.startsWith("image/") ? (
                              /* Image message - show larger image */
                              <div>
                                <img
                                  src={request.fileUrl}
                                  alt={request.fileName}
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    marginBottom: request.hasText ? "8px" : "0",
                                  }}
                                  onClick={() =>
                                    openFileModal(
                                      request.fileUrl,
                                      "image",
                                      request.fileName
                                    )
                                  }
                                  onError={(e) => {
                                    // Fallback to smaller thumbnail if main image fails
                                    e.target.style.maxWidth = "60px";
                                    e.target.style.maxHeight = "60px";
                                  }}
                                />
                                {/* Show text below image if exists */}
                                {request.hasText &&
                                  request.user_query &&
                                  !request.user_query.includes(
                                    "Shared image:"
                                  ) &&
                                  !request.user_query.includes(
                                    "Shared file:"
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        marginTop: "4px",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: formatResponseMessage(request.user_query),
                                      }}
                                    />
                                  )}
                              </div>
                            ) : (
                              /* Non-image file message - show with icon */
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    background: "rgba(255,255,255,0.1)",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    marginBottom: request.hasText ? "8px" : "0",
                                  }}
                                  onClick={() => {
                                    // Check if it's a PDF or other file type
                                    if (
                                      request.fileUrl.includes(".pdf") ||
                                      request.fileName
                                        .toLowerCase()
                                        .includes(".pdf")
                                    ) {
                                      openFileModal(
                                        request.fileUrl,
                                        "pdf",
                                        request.fileName
                                      );
                                    } else {
                                      openFileModal(
                                        request.fileUrl,
                                        "other",
                                        request.fileName
                                      );
                                    }
                                  }}
                                >
                                  {getFileIcon(request.fileName, 24)}
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {request.fileName}
                                    </div>
                                  </div>
                                </div>
                                {/* Show text below file if exists */}
                                {request.hasText &&
                                  request.user_query &&
                                  !request.user_query.includes(
                                    "Shared image:"
                                  ) &&
                                  !request.user_query.includes(
                                    "Shared file:"
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        marginTop: "4px",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: formatResponseMessage(request.user_query),
                                      }}
                                    />
                                  )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: "flex", alignItems: "start" }}>
                            <span
                              style={{ flex: 1, paddingRight: "12px" }}
                              dangerouslySetInnerHTML={{
                                __html: formatResponseMessage(
                                  formatMentionSpacing(
                                    request.user_query ||
                                      "User message text goes here."
                                  )
                                ),
                              }}
                            />
                            {/* Copy and Edit icons for user text message */}
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginLeft: "8px",
                                gap: "8px",
                              }}
                            >
                              {/* Copy icon */}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                onClick={() => {
                                  let textToCopy = request.user_query;
                                  const selectedText = window
                                    .getSelection()
                                    .toString();
                                  if (selectedText) {
                                    textToCopy = selectedText;
                                  }
                                  navigator.clipboard.writeText(textToCopy);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Copy message"
                              >
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                              </svg>
                              {/* Pencil (edit) icon */}
                              <i
                                className="bi bi-pencil-fill"
                                style={{ fontSize: "12px", cursor: "pointer" }}
                                title="Edit message"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReplyMessage(request.user_query);
                                  if (textareaRef.current) {
                                    textareaRef.current.focus();
                                  }
                                }}
                              ></i>
                            </span>
                          </div>
                        )}
                      </div>
                      {/* {!request.isProcessing && !request.isAudioOnly && (
                        <div style={{ fontSize: "12px", opacity: 0.7, marginTop: "4px" }}>
                          {getTimeZone(request.createdAt) || "12:01 PM"}
                        </div>
                      )} */}
                    </div>
                  </div>
                  ) : null}

                  {/* Bot message (left) - Only show for text messages, not audio-only */}
                  {!request.isAudioOnly && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        marginBottom: "12px",
                        width: "100%",
                        position: "relative", // <-- Needed for dropdown positioning
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "transparent",
                          color: "var(--text)",
                          padding: "12px 16px 12px 45px",
                          borderRadius: "12px",
                          maxWidth: "100%",
                          wordBreak: "break-word",
                        }}
                      >
                        {/* 3-dot dropdown START */}
                        <div style={{ position: "absolute", top: 8, right: 8 }}>
                          <div className="dropdown">
                            <i
                              className="bi bi-three-dots-vertical"
                              style={{ cursor: "pointer", fontSize: "1.2em" }}
                              id={`dropdownMenuButton-meeting-${index}`}
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            ></i>
                            <ul
                              className="dropdown-menu"
                              aria-labelledby={`dropdownMenuButton-meeting-${index}`}
                            >
                              <li
                                className="dropdown-item"
                                onClick={() => {
                                  setShowAddToSessionModal(true);
                                  setAddToChatText(request.response || "");
                                }}
                              >
                                <i className="bi bi-plus-square-dotted"></i>
                                &nbsp;Add to Topic
                              </li>
                            </ul>
                          </div>
                        </div>
                        {/* 3-dot dropdown END */}
                        <span
                          className="zoomey-title"
                          style={{ fontWeight: "bold", marginLeft: "-47px" }}
                        >
                          <span className="chatbot_z_logo">Z</span>
                          Zoomey
                        </span>
                        <div style={{ paddingRight: "40px" }}>
                          {request?.response ? (
                            <div dangerouslySetInnerHTML={{ __html: formatResponseMessage(request.response) }} />
                          ) : (() => {
                            // Check if this is the last message
                            const isLastMessage = index === selectedMeeting.length - 1;
                            // Check if request is older than 1 minute
                            const requestTime = moment.utc(request.createdAt);
                            const now = moment();
                            const isOlderThanOneMinute = now.diff(requestTime, 'minutes') >= 1;

                            if (isLastMessage && !isOlderThanOneMinute) {
                              return (
                                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                  <span>{responseInformMessage}</span>
                                  <span style={{ display: "flex", gap: "3px", marginLeft: "2px" }}>
                                    <span style={{ 
                                      width: "6px", height: "6px", borderRadius: "50%", 
                                      background: "var(--text)", 
                                      animation: "bounce 1.4s infinite ease-in-out" 
                                    }}></span>
                                    <span style={{ 
                                      width: "6px", height: "6px", borderRadius: "50%", 
                                      background: "var(--text)", 
                                      animation: "bounce 1.4s infinite ease-in-out",
                                      animationDelay: "-0.32s"
                                    }}></span>
                                    <span style={{ 
                                      width: "6px", height: "6px", borderRadius: "50%", 
                                      background: "var(--text)", 
                                      animation: "bounce 1.4s infinite ease-in-out",
                                      animationDelay: "-0.16s"
                                    }}></span>
                                  </span>
                                  <style>{`
                                    @keyframes bounce {
                                      0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
                                      40% { transform: scale(1); opacity: 1; }
                                    }
                                  `}</style>
                                </div>
                              );
                            }
                            return <span style={{ color: "#888", fontStyle: "italic" }}>⏹️ You stopped this request</span>;
                          })()}
                        </div>
                        {/* <br /> */}
                        <span>
                          {getTimeZone(request.created_at)}
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <> </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "8px",
            }}
          >
            {/* File preview section - exactly like ReplyModal */}
            {filePreviews.length > 0 && (
              <div
                style={{
                  maxHeight: "120px",
                  overflowY: "auto",
                  padding: "8px",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  background: "#f8f9fa",
                }}
              >
                {filePreviews.map((preview, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#fff",
                      borderRadius: "8px",
                      margin: "4px 0",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    {preview.type === "image" ? (
                      <>
                        <img
                          src={preview.url}
                          alt={preview.name}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "2px solid #e0e0e0",
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            marginLeft: "12px",
                            fontSize: "14px",
                            color: "#495057",
                          }}
                        >
                          {preview.name}
                        </span>
                      </>
                    ) : (
                      <>
                        {getFileIcon(preview.name, 28)}
                        <span
                          style={{
                            flex: 1,
                            marginLeft: "12px",
                            fontSize: "14px",
                            color: "#495057",
                          }}
                        >
                          {preview.name}
                        </span>
                      </>
                    )}
                    <button
                      onClick={() => removeFile(index)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "18px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                      }}
                      title="Remove file"
                      onMouseEnter={(e) =>
                        (e.target.style.background = "#f8f9fa")
                      }
                      onMouseLeave={(e) => (e.target.style.background = "none")}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input section */}
            {isAudioProcessing && copyToTextArea && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "3px solid var(--teal-bg)",
                      borderRadius: "50%",
                      animation: "spin .5s linear infinite",
                    }}
                  ></div>
                  <i
                    className="bi bi-mic-fill"
                    style={{
                      fontSize: "14px",
                      opacity: 0.7,
                      color: "var(--text)",
                    }}
                  ></i>
                  <span
                    style={{
                      fontSize: "12px",
                      opacity: 0.7,
                      color: "var(--text)",
                    }}
                  >
                    Processing your voice...
                  </span>
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {/* Meeting/Zoomey Mode Toggle Button */}
              <button
                type="button"
                onClick={() => {
                  if (meetingBtnDisabled) return;
                  setMeetingEnabled((prev) => {
                    const next = !prev;
                    if (next) textareaRef.current?.focus();
                    return next;
                  });
                }}
                disabled={meetingBtnDisabled}
                title={
                  meetingBtnDisabled
                    ? "Processing..."
                    : meetingEnabled
                    ? "Meeting Mode ON - Click for Full Zoomey"
                    : "Zoomey Mode - Click for Meeting Focus"
                }
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: meetingEnabled 
                    ? "none" 
                    : "1px solid var(--border-color)",
                  background: meetingEnabled 
                    ? "var(--teal-bg)" 
                    : "transparent",
                  cursor: meetingBtnDisabled ? "not-allowed" : "pointer",
                  opacity: meetingBtnDisabled ? 0.6 : 1,
                  transition: "all 0.2s ease",
                  padding: 0,
                }}
              >
                {meetingEnabled ? (
                  // Meeting icon when enabled - people group
                  <i 
                    className="bi bi-people-fill" 
                    style={{ 
                      fontSize: "18px", 
                      color: "#fff" 
                    }} 
                  />
                ) : (
                  // Zoomey Z icon when disabled
                  <span
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: "var(--teal-bg)",
                      fontFamily: "sans-serif",
                    }}
                  >
                    Z
                  </span>
                )}
              </button>
              {!meetingEnabled && <ModeSelector onModeChange={setSelectedMode} />}
              <MentionsInput
                ref={mentionsInputRef}
                inputText={replyMessage}
                setInputText={async (text) => {
                  setReplyMessage(text);
                  // Save draft on every change
                }}
                textareaRef={textareaRef}
                onPasteFile={(file, preview) => {
                  setSelectedFiles([file]);
                  setFilePreviews([preview]);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (filePreviews.length > 0) {
                      handleSendWithFiles();
                    } else if (replyMessage.trim() !== "" || mentionsInputRef.current?.getEffectiveQuery()) {
                      const q = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
                      mentionsInputRef.current?.clearMention();
                      sendChatMessage(q);
                    }
                  }
                }}
                placeholder="What should I help with in this meeting context?"
                // className="form-control"
                style={{ resize: "none", flex: 1 }}
                rows={4}
              />
              {!audioRecording && (
                <>
                  <button
                    onClick={() => {
                      if (isSending || isStreaming) {
                        handleStopRequest();
                      } else if (filePreviews.length > 0) {
                        handleSendWithFiles();
                      } else {
                        const q = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
                        mentionsInputRef.current?.clearMention();
                        sendChatMessage(q);
                      }
                    }}
                    disabled={
                      isLoading ||
                      (!isSending &&
                        !isStreaming &&
                        replyMessage.trim() === "" &&
                        filePreviews.length === 0)
                    }
                    style={{
                      border: "none",
                      background: isSending || isStreaming ? "#dc3545" : "var(--teal-bg)",
                      padding: "8px",
                      borderRadius: "8px",
                      width: "40px",
                      height: "40px",
                    }}
                    title={isSending || isStreaming ? "Stop generating" : "Send message"}
                  >
                    {isSending || isStreaming ? (
                      <i className="bi bi-stop-fill"></i>
                    ) : (
                      <i className="bi bi-send"></i>
                    )}
                  </button>

                  {/* File upload button - exactly like ReplyModal */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    accept="image/*,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      border: "1px solid var(--border-color)",
                      background: "transparent",
                      color: "var(--text)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      opacity: isLoading || isSending || isStreaming ? 0.5 : 1,
                    }}
                    title="Attach file"
                    disabled={isLoading || isSending || isStreaming}
                  >
                    <i
                      className="bi bi-paperclip"
                      style={{ fontSize: "20px", cursor: "pointer" }}
                    ></i>
                  </button>
                </>
              )}

              {/* Add Record Component for Meeting Modal */}
              <Record
                onStop={handleMeetingAudioRecorded}
                setAudioInput={setAudioInput}
                setAudioRecording={setAudioRecording}
                isLoading={isLoading || isSending || isStreaming}
                setIsUserQueyLoading={setIsUserQueyLoading}
                isDarkModeInternal={isDarkModeInternal}
                setCopyToTextArea={setCopyToTextArea}
                setCancelAudio={setCancelAudio}
                // setIsAudioProcessing={setIsAudioProcessing}
                //  setIsAudioProcessing={setIsAudioProcessing}
                setIsVoiceProcessing={setIsAudioProcessing} // <-- ADD THIS LINE
              />
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      <AddToSessionModal
        show={showAddToSessionModal}
        handleClose={() => setShowAddToSessionModal(false)}
        addToChatText={addToChatText}
        userId={userId}
      />

      <MeetingCreatesessionmodal
        show={showMeetingSessionModal}
        handleClose={() => setShowMeetingSessionModal(false)}
        apiUrl={zoomeyApiUrl}
        sideConvoList={sideConvoList}
        meetingId={meeting_summaries_id?.id}
        meeting_summaries_id={meeting_summaries_id}
        loginUserID={userId}
        apiToken={apiToken}
      />

      {/* Common File Viewer Modal - Handles both Images and PDFs */}
      <ImageviewModal
        show={showFileModal}
        onHide={() => setShowFileModal(false)}
        fileUrl={selectedFileUrl}
        fileType={selectedFileType}
        fileName={selectedFileName}
        altText="Meeting Chat File Preview"
      />
    </>
  );
}

export default MeetingChatModal;
